import React, { useState } from 'react';
import {
    setChangeRequestParticipantReadyState,
    getChangeRequestOffers,
    reopenChangeRequest,
    updateChangeRequestAttachment,
} from '@/services/change-requests';
import { Trans } from '@lingui/react/macro';
import ChangeRequestParticipantList from '../ChangeRequestParticipantList';
import ChangeRequestParticipantOfferDialog from '../ChangeRequestParticipantOfferDialog';
import theme from './ChangeRequestDetails.module.scss';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { AlertBanner, Button, ButtonBar, ConfirmButton } from '@ygtechnologies/rubus-ui';
import { faEnvelope, faHistory, faFilePlus, faFileTimes, faFileMinus } from '@fortawesome/pro-light-svg-icons';
import {
    ChangeRequest,
    SellerChangeRequestStatus,
    ChangeRequestParticipantInfo,
    BatchedChanges,
    UpdateFields,
    BuyerOfferStatus,
    ChangedItem,
    ChangeRequestAttachment,
} from '@/types';
import { SimpleCard } from '@/components/Cards';
import { DateDisplay } from '@/components/localization';
import ChangeRequestSendDialog from '../ChangeRequestSendDialog/ChangeRequestSendDialog';
import ChangeRequestRetractOfferDialog from '../ChangeRequestRetractOfferDialog';
import ChangeRequestCloseDialog from '../ChangeRequestCloseDialog';
import ChangeRequestOfferHistoryDialog from '../ChangeRequestOfferHistoryDialog';
import { ProjectPermissionToggle } from '@/components/permissions';
import { isChangeRequestLocked } from '.';
import { useAsyncState } from '@/utils/hooks';
import ChangeRequestRequestNewOffersDialog from '../ChangeRequestRequestNewOffersDialog';
import ChangeRequestDetailsTotalRow from './ChangeRequestDetailsTotalRow';
import Banner from '@/components/Banner';
import ChangeRequestParticipantChangeModal from '../ChangeRequestParticipantChangeModal';
import { CardSection } from '@ygtechnologies/bygr-frontend-component-library';

interface ChangeRequestDetailsOfferTabProps {
    changeRequest: ChangeRequest;
    participants: ChangeRequestParticipantInfo[];
    onParticipantChange?(changes: BatchedChanges<ChangeRequestParticipantInfo>): void;
    onChange(changes: BatchedChanges<ChangeRequest>): void;
}

const ChangeRequestDetailsOfferTab: React.FC<ChangeRequestDetailsOfferTabProps> = ({
    changeRequest,
    participants,
    onParticipantChange,
    onChange,
}) => {
    const [offerDialogParticipant, setOfferDialogParticipant] = useState<ChangeRequestParticipantInfo | null>(null);
    const [participantChangeFrom, setParticipantChangeFrom] = useState<ChangeRequestParticipantInfo | null>(null);
    const [showSendDialog, setShowSendDialog] = useState(false);
    const [showCloseDialog, setShowCloseDialog] = useState(false);
    const [showRetractDialog, setShowRetractDialog] = useState(false);
    const [showHistoryDialog, setShowHistoryDialog] = useState(false);
    const [showRequestOffersDialog, setShowRequestOffersDialog] = useState(false);

    const [offers, , reloadOffers] = useAsyncState(() => getChangeRequestOffers(changeRequest.id), [], null);

    const triggerChange = (changes: UpdateFields<ChangeRequest>) => {
        triggerHandler(onChange, BatchedChanges.single(changeRequest.id, changes));
    };

    const triggerReadyStateChange = (participantsToChange: ChangeRequestParticipantInfo[], isReady: boolean) => {
        const participantChanges = BatchedChanges.repeat<ChangeRequestParticipantInfo>(
            participantsToChange.map((x) => x.id),
            { isReadyToSend: isReady }
        );

        triggerHandler(onParticipantChange, participantChanges);
    };

    const handleReopenChangeRequest = async () => {
        await reopenChangeRequest(changeRequest.id);

        const participantChanges = BatchedChanges.repeat<ChangeRequestParticipantInfo>(
            participants.map((x) => x.id),
            { isReadyToSend: false }
        );

        triggerChange({ isClosed: false });
        triggerHandler(onParticipantChange, participantChanges);
    };

    const handleReadyStateChange = (participant: ChangeRequestParticipantInfo, isReady: boolean) => {
        setChangeRequestParticipantReadyState(changeRequest.id, participant.userId, isReady);
        triggerReadyStateChange([participant], isReady);
    };

    const handleAttachmentChange = async (
        participant: ChangeRequestParticipantInfo,
        change: ChangedItem<ChangeRequestAttachment>
    ) => {
        await updateChangeRequestAttachment(change.modified.id, {
            hideFromBuyer: change.modified.hideFromBuyer,
        });

        const participantChanges = BatchedChanges.single<ChangeRequestParticipantInfo>(participant.id, {
            offerAttachments: change.updateArray(participant.offerAttachments),
        });

        triggerHandler(onParticipantChange, participantChanges);
    };

    const handleRequestNewOffers = () => {
        triggerChange({ status: SellerChangeRequestStatus.AwaitingOffers });
        triggerReadyStateChange(participants, false);
    };

    const isLocked = isChangeRequestLocked(changeRequest);
    // const totalPrice = sumBy(participants, 'offerFinalPrice');

    const isEmpty = participants.length === 0 || participants.every((x) => x.offerPrice === null);

    const latestActiveOffer = offers
        ?.reverse()
        .find((o) => o.status === BuyerOfferStatus.Pending || o.status === BuyerOfferStatus.Accepted);

    const isClosable = [
        SellerChangeRequestStatus.Unassigned,
        SellerChangeRequestStatus.Rejected,
        SellerChangeRequestStatus.CanceledBySeller,
        SellerChangeRequestStatus.AwaitingOffers,
        SellerChangeRequestStatus.ReadyToSend,
    ].includes(changeRequest.status);

    const canRequestNewOffers = [SellerChangeRequestStatus.Rejected, SellerChangeRequestStatus.CanceledBySeller].includes(
        changeRequest.status
    );

    const hasHiddenPrices = !participants || participants.some((x) => x.hasLines && x.priceRedacted);
    const totalsVisible = participants && !hasHiddenPrices;

    return (
        <React.Fragment>
            <AlertBanner type="info">
                <Trans>
                    Make sure that all participants that need to provide an offer has provided their input on this change
                    request prior to sending to the client.
                </Trans>
            </AlertBanner>

            {latestActiveOffer && (
                <ProjectPermissionToggle condition="canSendChangeRequests">
                    <SimpleCard variant="flat">
                        <CardSection minWidth="100px" caption={<Trans>Offer Id</Trans>}>
                            #{latestActiveOffer.id}
                        </CardSection>
                        <CardSection className={theme.offerMessage} caption={<Trans>Offer message</Trans>}>
                            {latestActiveOffer.sendNote}
                            <div>
                                <small>
                                    <Trans>
                                        Sent by {latestActiveOffer.sentByUserName} on{' '}
                                        <DateDisplay value={latestActiveOffer.sendDate as Date} />
                                    </Trans>
                                </small>
                            </div>
                        </CardSection>
                    </SimpleCard>
                </ProjectPermissionToggle>
            )}

            <ChangeRequestParticipantList
                participants={participants}
                onParticipantChangeClick={(p) => setParticipantChangeFrom(p)}
                onGiveOfferClick={(p) => setOfferDialogParticipant(p)}
                onReadyStateChange={handleReadyStateChange}
                onAttachmentChange={handleAttachmentChange}
                readOnly={isLocked}
            />

            {/* {!hasHiddenPrices && (
                <SimpleCard className={theme.totalCard}>
                    <div className={theme.totalCaption}>
                        <Trans>Total</Trans>
                    </div>

                    <CardSection right>
                        <span className={theme.totalPrice}>
                            <ProjectCurrencyDisplay value={totalPrice} currency="NOK" />
                        </span>
                    </CardSection>
                </SimpleCard>
            )} */}

            {totalsVisible && <ChangeRequestDetailsTotalRow participants={participants} />}

            <ButtonBar margins="top">
                <ButtonBar.Right>
                {changeRequest.hasOffers && (
                    <Button variant="tertiary" icon={faHistory} onClick={() => setShowHistoryDialog(true)}>
                        <Trans>Show earlier offers</Trans>
                    </Button>
                )}

                <ProjectPermissionToggle condition="canSendChangeRequests">
                    {isClosable && (
                        <Button variant="tertiary" icon={faFileTimes} onClick={() => setShowCloseDialog(true)}>
                            <Trans>Close request</Trans>
                        </Button>
                    )}

                    {changeRequest.status === SellerChangeRequestStatus.SentToBuyer && (
                        <Button variant="secondary" icon={faFileMinus} onClick={() => setShowRetractDialog(true)}>
                            <Trans>Retract offer</Trans>
                        </Button>
                    )}

                    {canRequestNewOffers && (
                        <Button variant="secondary" icon={faFilePlus} onClick={() => setShowRequestOffersDialog(true)}>
                            <Trans>Request new offers</Trans>
                        </Button>
                    )}

                    {!isLocked && (
                        <Button
                            variant="primary"
                            icon={faEnvelope}
                            onClick={() => setShowSendDialog(true)}
                            disabled={isEmpty || participants.some((x) => !x.isReadyToSend)}>
                            <Trans>Send offer</Trans>
                        </Button>
                    )}

                    {changeRequest.status === SellerChangeRequestStatus.Closed && (
                        <ConfirmButton
                            dialogTitle={<Trans>Reopen change request #{changeRequest.id}</Trans>}
                            dialogBody={
                                <>
                                    <Trans>Are you sure you want to reopen this change request?</Trans>
                                    {changeRequest.externalAgreementId && (
                                        <p>
                                            <Trans>
                                                This will also set the corresponding external agreement to approved.
                                            </Trans>
                                        </p>
                                    )}
                                </>
                            }
                            variant="secondary"
                            icon={faEnvelope}
                            onConfirm={handleReopenChangeRequest}>
                            <Trans>Reopen request</Trans>
                        </ConfirmButton>
                    )}
                </ProjectPermissionToggle>
                </ButtonBar.Right>
            </ButtonBar>

            <ChangeRequestParticipantOfferDialog
                changeRequest={changeRequest}
                participant={offerDialogParticipant}
                show={!!offerDialogParticipant}
                onClose={() => setOfferDialogParticipant(null)}
                onSave={onParticipantChange}
                onReadyStateChange={handleReadyStateChange}
            />

            <ChangeRequestSendDialog
                participants={participants}
                changeRequest={changeRequest}
                show={showSendDialog}
                onClose={() => setShowSendDialog(false)}
                onSave={(e) => {
                    triggerChange({
                        status: SellerChangeRequestStatus.SentToBuyer,
                        hasOffers: true,
                        optionGroupOverrides: e.optionGroupOverrides,
                    });
                    reloadOffers();
                }}
            />

            <ChangeRequestRetractOfferDialog
                changeRequestId={changeRequest.id}
                show={showRetractDialog}
                onClose={() => setShowRetractDialog(false)}
                onSave={() => {
                    triggerChange({ status: SellerChangeRequestStatus.ReadyToSend });
                    reloadOffers();
                }}
            />

            <ChangeRequestCloseDialog
                changeRequestId={changeRequest.id}
                show={showCloseDialog}
                onClose={() => setShowCloseDialog(false)}
                onSave={(note) => {
                    triggerChange({ status: SellerChangeRequestStatus.Closed, isClosed: true, closeNote: note });
                }}
            />

            <ChangeRequestRequestNewOffersDialog
                changeRequestId={changeRequest.id}
                show={showRequestOffersDialog}
                onClose={() => setShowRequestOffersDialog(false)}
                onSave={handleRequestNewOffers}
            />

            <ChangeRequestOfferHistoryDialog
                offers={offers}
                changeRequestId={changeRequest.id}
                show={showHistoryDialog}
                onClose={() => setShowHistoryDialog(false)}
            />
            {participantChangeFrom && (
                <ChangeRequestParticipantChangeModal
                    onSubmit={(changes) => {
                        triggerHandler(onParticipantChange, BatchedChanges.single(participantChangeFrom.id, changes));
                        setParticipantChangeFrom(null);
                    }}
                    oldParticipant={participantChangeFrom}
                    isOpen={!!participantChangeFrom}
                    toggle={() => setParticipantChangeFrom(null)}
                />
            )}
        </React.Fragment>
    );
};

export default ChangeRequestDetailsOfferTab;
